<!-- Represents a mapping of a Spot to a Decoder, displaying Spot details. -->

<template>
  <div
    data-cy="spot-entry"
    :class="{
      linked: !!entry.play_after_entry,
      mobile: $vuetify.breakpoint.xsOnly,
    }"
  >
    <v-list
      dense
      two-line
      :class="{
        'bg-warning': isActiveViaTags && !entry.active,
      }"
    >
      <v-list-tile
        class="
          entry-values entry-tile
          mt-1
          no-hover-clr no-click-pointer
          list-tile-align-start
        "
        :ripple="false"
      >
        <!-- Note: Don't add 'grow-0' here as it looks wrong on iOS -->
        <div
          class="entry-item-group mb-1"
          style="height: 100%"
        >
          <v-layout>
            <transition
              name="slide-x-transition"
              mode="out-in"
            >
              <v-layout
                v-if="showSelectionCheckbox && entry.selectable"
                class="mr-2"
                align-baseline
              >
                <v-checkbox
                  v-model="entry.selected"
                  color="msr"
                  hide-details
                />
              </v-layout>
            </transition>
            <!-- Preview -->
            <div
              v-if="showPreview"
              class="entry-item"
              :class="{
                tvpreview: ctype === 'tv',
                radiopreview: ctype === 'radio',
                clickable: enablePreview,
              }"
              :style="{opacity: opacity}"
            >
              <mini-preview-unprocessed
                v-if="!entry.spot.processed || !entry.processed"
                :ctype="entry.ctype"
                :closingtime="entry.spot.closingtime"
                small
                show-tv-as-text
              />
              <div
                v-else
                style="position: relative"
              >
                <mini-preview-audio
                  v-if="ctype === 'radio'"
                  :enabled="enablePreview"
                  :closingtime="entry.spot.closingtime"
                  :file-url="entry.spot.file_url"
                  small
                />
                <mini-preview-video
                  v-if="entry.ctype === 'tv'"
                  :spot="entry.spot"
                  :tvformat="entry.spot.tvformat"
                  class="clickable"
                  small
                  allow-portrait-adaptation
                  @started="videoPreviewRunning = true"
                  @stopped="videoPreviewRunning = false"
                />
              </div>
            </div>
          </v-layout>
        </div>
        <div class="entry-item-group grow-3">
          <!-- Category -->
          <div
            v-if="showCategory"
            class="entry-item category grey--text text--darken-1"
            :class="{'mr-2': $vuetify.breakpoint.smAndUp}"
            :style="{'max-width': $vuetify.breakpoint.xsOnly ? null : '84px'}"
          >
            <span v-if="entry.spot.category">{{
              entry.spot.category.name
            }}</span>
          </div>
          <!-- Title -->
          <div
            v-if="showTitle"
            class="entry-item grow-3"
          >
            <!-- Expired -->
            <div
              v-if="entry.spot && entry.spot.expired"
              class="warning--text"
            >
              {{ $t('expiredContentShort') }}
            </div>
            <v-layout
              wrap
              :style="{opacity: opacity}"
            >
              <multiformat-bundle-indicator
                v-if="entry.bundle && entry.bundle.has_multiple_formats"
                is-multiformat-bundle
                class="mr-2"
              />
              <span
                style="font-size: 1.15em"
                class="clickable pseudo-anchor--inverted"
                :class="{
                  'font-weight-bold mt-1':
                    entry.bundle && entry.bundle.has_multiple_formats,
                  'msr--text':
                    showSelectionCheckbox && entry.selectable && entry.selected,
                }"
                @click="goToSpot(ctype, entry.bundle_id || entry.spot.id)"
              >{{ entry.spot.title }}</span>
            </v-layout>
            <!-- Matching Tags info -->
            <matching-tags
              v-if="isActiveViaTags"
              class="pt-1"
              :matching-tags="entry.through_matching_tags"
              :tag-entry-active="entry.active"
              @reactivate="toggleActivateTagEntry(entry.ctype, entry)"
            />
            <!-- Active via Automation info -->
            <via-automation
              v-if="entry.via_automation"
              class="pt-1"
              :entry="entry"
            />
          </div>
          <!-- Closingtime Weekdays & Time -->
          <entry-closingtime
            v-if="entry.id && entry.spot.closingtime"
            :entry="entry"
            class="nowrap entry-item grow-1 timing"
            :class="{clickable: showActions && updateAllowed}"
            @click.native="
              showActions && updateAllowed
                ? (showEntryTimingDialog = true)
                : null
            "
          />
          <!-- Timing related info -->
          <div
            v-if="showTiming"
            class="entry-item grow-1 timing"
            :class="getTimingCSS(entry, false)"
          >
            <div
              :class="{clickable: showActions && updateAllowed}"
              @click="
                showActions && updateAllowed
                  ? (showEntryTimingDialog = true)
                  : null
              "
            >
              <!-- Start/Stopdate -->
              <span
                v-if="entry.has_timing"
                class="caption"
              >
                {{ entry.startdate | dayjs('DD.MM.YYYY') }}
                <span
                  v-if="entry.stopdate !== null"
                >- {{ entry.stopdate | dayjs('DD.MM.YYYY') }}</span>
              </span>
              <!-- Timeframe -->
              <entry-timeframe
                :timeframes="entry.timeframes || []"
                data-cy="entry-timeframe"
              />
              <!-- Play after Entry -->
              <play-after-entry-status
                :class="{clickable: showActions && updateAllowed}"
                :entry="entry"
                @click.native.stop="
                  showActions && updateAllowed
                    ? (showLinkContentsDialog = true)
                    : null
                "
              />
            </div>
          </div>
        </div>
        <!-- SpotEntry Actions Menu -->
        <div class="entry-item-group grow-0 last">
          <v-layout
            column
            align-center
          >
            <v-menu
              v-if="showActions && atLeastOneActionVisible"
              offset-y
              left
            >
              <v-icon
                slot="activator"
                :medium="$vuetify.breakpoint.xsOnly"
                class="unselectable"
                data-cy="spotentry-menu-activator"
              >
                more_vert
              </v-icon>
              <v-list>
                <!-- Change timing -->
                <v-list-tile
                  v-if="showTimingAction"
                  @click="showEntryTimingDialog = true"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      :class="{
                        'msr--text': entry.has_timing,
                        'grey--text': !entry.has_timing,
                      }"
                    >
                      timer
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('timing') }}
                </v-list-tile>
                <!-- Toggle hotrotation -->
                <v-list-tile
                  v-if="showToggleHotRotationAction"
                  @click="toggleEntryHotRotation(entry)"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      v-if="!entry.hotrotation"
                      class="grey--text"
                    >
                      star_outline
                    </v-icon>
                    <v-icon
                      v-else
                      class="msr--text"
                    >
                      star
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('hotrotation') }}
                </v-list-tile>
                <!-- Edit overlay data -->
                <v-list-tile
                  v-if="showEditOverlayDataAction"
                  @click="showEditOverlayDataDialog = true"
                >
                  <v-list-tile-avatar>
                    <v-icon class="grey--text">
                      edit
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('customize') }}
                </v-list-tile>
                <!-- Link contents -->
                <v-list-tile
                  v-if="showLinkContentsAction"
                  @click="showLinkContentsDialog = true"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      :class="{
                        'msr--text': entry.play_after_entry,
                        'grey--text': !entry.play_after_entry,
                      }"
                    >
                      link
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('linkContentsShort') }}
                </v-list-tile>
                <!-- Deactivate -->
                <v-list-tile
                  v-if="showRemovalAction"
                  @click="showRemovalDialog(entry)"
                >
                  <v-list-tile-avatar>
                    <v-icon class="grey--text">
                      block
                    </v-icon>
                  </v-list-tile-avatar>
                  {{ $t('deactivate') }}
                </v-list-tile>
                <!-- Deactivate tagged content -->
                <v-list-tile
                  v-if="showTagEntryDeactivationAction"
                  data-cy="spotentry-menu-toggle-tagentry-active"
                  @click="toggleActivateTagEntry(entry.ctype, entry)"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      v-if="entry.active"
                      class="grey--text"
                    >
                      block
                    </v-icon>
                    <v-icon
                      v-else
                      class="grey--text"
                    >
                      play_arrow
                    </v-icon>
                  </v-list-tile-avatar>
                  <span v-if="entry.active">{{ $t('deactivate') }}</span>
                  <span v-else>{{ $t('reactivate') }}</span>
                </v-list-tile>
              </v-list>
            </v-menu>
            <div
              v-if="showStatus"
              class="spot-status"
            >
              <div v-if="entry.has_timing">
                <v-icon
                  class="msr--text mt-1"
                  :class="{clickable: showActions && updateAllowed}"
                  style="font-size: 1.25em"
                  @click="
                    showActions && updateAllowed
                      ? (showEntryTimingDialog = true)
                      : null
                  "
                >
                  timer
                </v-icon>
              </div>
              <div v-if="entry.hotrotation">
                <v-icon
                  class="msr--text mt-1"
                  :class="{clickable: showActions && updateAllowed}"
                  style="font-size: 1.25em"
                  @click="
                    showActions && updateAllowed
                      ? toggleEntryHotRotation(entry)
                      : null
                  "
                >
                  star
                </v-icon>
              </div>
              <div
                v-if="entry.has_timing || entry.hotrotation"
                class="mt-1"
              />
            </div>
          </v-layout>
        </div>
      </v-list-tile>
      <!-- Overlay Data for this TVSpotEntry -->
      <overlay-data-list-tile
        v-if="hasOverlayData"
        :overlay-options="entry.spot.overlay_options"
        :overlay-data="entry.overlay_data"
        :disabled="!updateAllowed"
        @click="updateAllowed ? (showEditOverlayDataDialog = true) : null"
      />
    </v-list>
    <!-- Entry Timing Dialog -->
    <entry-timing-dialog
      :show.sync="showEntryTimingDialog"
      :entries="[entry]"
      :show-timeframe-controls="!isClosingTimeRadioSpotEntry(entry)"
      @submitted="$emit('changed')"
    />
    <!-- Removal Dialog -->
    <v-dialog
      v-model="showConfirmRemovalDialog"
      absolute
    >
      <v-card>
        <v-card-title>
          <v-btn
            style="position: absolute; top: 0; right: 0"
            flat
            icon
            :title="$t('close')"
            @click="showConfirmRemovalDialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
          <div class="headline">
            {{ $t('askDeleteEntry') }}
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            @click="removeEntry(entryToRemove.ctype, entryToRemove)"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Edit OverlayData Dialog -->
    <edit-overlay-data-dialog
      :show.sync="showEditOverlayDataDialog"
      :entries="[entry]"
    />
    <!-- Link Contents Dialog -->
    <link-contents-dialog
      v-if="ctype === 'tv' && showLinkContentsDialog"
      :show.sync="showLinkContentsDialog"
      :entry="entry"
      :entry-choices="otherEntries"
      @changed="$emit('changed')"
    />
    <v-divider v-if="showDivider" />
  </div>
</template>

<script>
import {isClosingTimeRadioSpotEntry} from '@/mixins/identifiers.js';
import commonEntry from '@/mixins/commonEntry.js';
import spotentryUpdateOnContentProcessed from '@/mixins/spotentryUpdateOnContentProcessed.js';

import BtnOk from '@/components/ButtonOk';
import EditOverlayDataDialog from '@/components/EditOverlayDataDialog';
import EntryClosingtime from '@/components/EntryClosingtime';
import EntryTimeframe from '@/components/EntryTimeframe';
import EntryTimingDialog from '@/components/EntryTimingDialog';
import LinkContentsDialog from '@/components/LinkContentsDialog';
import MatchingTags from '@/components/MatchingTags';
import MiniPreviewAudio from '@/components/MiniPreviewAudio';
import MiniPreviewUnprocessed from '@/components/MiniPreviewUnprocessed';
import MiniPreviewVideo from '@/components/MiniPreviewVideo';
import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import OverlayDataListTile from '@/components/OverlayDataListTile';
import PlayAfterEntryStatus from '@/components/PlayAfterEntryStatus';
import ViaAutomation from '@/components/ViaAutomation.vue';

export default {
  components: {
    BtnOk,
    EditOverlayDataDialog,
    EntryClosingtime,
    EntryTimeframe,
    EntryTimingDialog,
    LinkContentsDialog,
    MatchingTags,
    MiniPreviewAudio,
    MiniPreviewUnprocessed,
    MiniPreviewVideo,
    MultiformatBundleIndicator,
    OverlayDataListTile,
    PlayAfterEntryStatus,
    ViaAutomation,
  },
  mixins: [commonEntry, spotentryUpdateOnContentProcessed],
  props: {
    /* A Radio- or TVSpotEntry object. */
    entry: {
      type: Object,
      required: true,
    },
    /**
     * Optional list of Radio/TVSpotEntry objects from the same Decoder.
     *
     * This is needed when linking a SpotEntry against one of the others.
     */
    allEntries: {
      type: Array[Object],
      default() {
        return [];
      },
    },
    /* 'radio' or 'tv' */
    ctype: {
      type: String,
      required: true,
    },
    /* Whether to enable the 'remove this entry' menu action. */
    removalAllowed: {
      type: Boolean,
      default: true,
    },
    /* Whether to enable the 'update hotrotation/timing' etc. menu actions. */
    updateAllowed: {
      type: Boolean,
      default: true,
    },
    /* Whether clicking the preview navigates to another page. */
    enablePreview: {
      type: Boolean,
      default: true,
    },
    /* All props below control column visibility. */
    showSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    showPreview: {
      type: Boolean,
      default: true,
    },
    showCategory: {
      type: Boolean,
      default: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    showTiming: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      videoPreviewRunning: false,
      showEntryTimingDialog: false,
      showEditOverlayDataDialog: false,
      showLinkContentsDialog: false,
    };
  },
  computed: {
    /**
     * Fade out a few things if we have a deactivated TagEntry.
     */
    opacity() {
      if (this.isActiveViaTags && !this.entry.active) {
        return 0.5;
      }
      return 1.0;
    },
    showTimingAction() {
      return this.updateAllowed && !this.isActiveViaTags;
    },
    showToggleHotRotationAction() {
      return (
        this.updateAllowed &&
        !this.isClosingTimeRadioSpotEntry(this.entry) &&
        !this.isActiveViaTags
      );
    },
    showEditOverlayDataAction() {
      return this.updateAllowed && this.hasOverlayData;
    },
    showLinkContentsAction() {
      return (
        this.ctype === 'tv' &&
        this.atLeastTwoEntries &&
        this.updateAllowed &&
        !this.isActiveViaTags
      );
    },
    showRemovalAction() {
      return this.removalAllowed && !this.isActiveViaTags;
    },
    showTagEntryDeactivationAction() {
      return (
        this.removalAllowed &&
        !!this.isActiveViaTags &&
        this.entry.spot.tag_entries_can_be_deactivated
      );
    },
    atLeastOneActionVisible() {
      return (
        this.showTimingAction ||
        this.showToggleHotRotationAction ||
        this.showEditOverlayDataAction ||
        this.showLinkContentsAction ||
        this.showRemovalAction ||
        this.showTagEntryDeactivationAction
      );
    },
    atLeastTwoEntries() {
      return this.allEntries.length > 1;
    },
    otherEntries() {
      return this.allEntries.filter((entry) => entry.id !== this.entry.id);
    },
    overlayData() {
      return this.entry.overlay_data;
    },
    hasOverlayData() {
      return this.overlayData && Object.keys(this.overlayData).length > 0;
    },
    isActiveViaTags() {
      return (
        this.entry.through_matching_tags &&
        this.entry.through_matching_tags.length > 0
      );
    },
  },
  mounted() {
    this.maybeRefetchDecoderOnContentProcessedEvent();
  },
  methods: {
    isClosingTimeRadioSpotEntry: isClosingTimeRadioSpotEntry,
    showRemovalDialog(entry) {
      if (!this.removalAllowed) {
        this.$bus.$emit('alert-warning', {
          message: 'tvPlayersNeedAtLeastOneClip',
          timeout: 4000,
          translate: true,
        });
        return;
      }
      this.entryToRemove = entry;
      this.showConfirmRemovalDialog = true;
    },
    goToSpot(ctype, spotId) {
      let path = '/spot/' + ctype + '/' + spotId;
      this.$router.push(path);
    },
  },
};
</script>

<style>
/* Ensure full width and auto height for Spot v-list-tile. */
.entry-tile .v-list__tile {
  width: 100%;
}
.v-list--two-line.v-list--dense .entry-tile .v-list__tile {
  height: auto;
}
</style>

<style scoped>
.linked {
  margin-left: 24px;
}

.linked.mobile {
  margin-left: 12px;
}

.back-btn {
  margin: 0;
  padding: 0;
}

.menu-item {
  display: flex;
  align-items: center;
}

.entry-headers {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  height: auto;
  padding: 0;
}

.entry-values {
  display: flex;
}

.entry-item {
  margin: 0.1em 0 0.1em 0;
}
.entry-item.timing {
  word-break: break-word;
}
.entry-item.last {
  margin-right: 0;
}
@media (max-width: 599px) {
  .entry-item {
    word-break: break-word;
  }
}

.entry-item-group {
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
}
.entry-item-group.last {
  justify-content: flex-end;
  align-items: flex-start;
}
@media (max-width: 959px) {
  .entry-item-group {
    flex-flow: column nowrap;
    align-items: flex-start;
  }
  .entry-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.category {
  min-width: 84px;
  word-break: break-word;
}

.tvpreview {
  display: flex;
  align-items: flex-start;
  margin-right: 1.5em;
}

.radiopreview {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-right: 1.5em;
}

i.radiopreview-icon {
  font-size: 4.5em !important;
}

.timing {
  display: flex;
  align-self: center;
}
@media (orientation: portrait) and (max-width: 599px) {
  .timing {
    align-self: start;
  }
}

.spot-status {
  height: 55px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
@media (max-width: 599px) {
  .spot-status {
    height: 75px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SpotEntry.vue